/**
 * Prévia 3D do ativo no detalhe e na ficha (US-4602, Sprint 46) — Nexora Grid (frontend).
 *
 * Criado em: 02/11/2026 · Última modificação: 02/11/2026 · Versão: 1.0.0
 *
 * Mostra o modelo do tipo do cadastro (poço, motobomba, reservatório, macromedidor, VRP, ponto de pressão) com o anel
 * da cor do estado. O motor (`previa3d.ts`, three.js) só carrega quando a prévia aparece. Sem WebGL, sem modelo para o
 * tipo (registro, ventosa, hidrante) ou com erro: o ícone do tipo e o texto — a tela nunca fica em branco.
 */
import { useEffect, useRef, useState } from "react";
import { modeloDoTipo, tipoDoCadastro, type ModeloMapa } from "../cco/regrasMapa";
import { IconeTipo } from "./IconeTipo";
import type { Estado } from "./Tela";
import type { Previa } from "./previa3d";

const COR_ESTADO: Partial<Record<Estado, string>> = { critico: "--alarme-critico", alto: "--alarme-alto", medio: "--alarme-medio",
  baixo: "--alarme-baixo", semcom: "--estado-sem-comunicacao" };

/** Modelo 3D do tipo do cadastro, ou `null` quando o tipo não tem modelo. */
export function modeloDoAtivo(tipo: string, identificacao?: string): ModeloMapa | null {
  const equipamento = tipoDoCadastro(tipo);
  return equipamento ? modeloDoTipo(equipamento, tipo, identificacao) : null;
}

function cor(el: HTMLElement, token: string): string {
  return getComputedStyle(el).getPropertyValue(token).trim() || "#888888";
}

export function PreviaAtivo3D({ tipo, identificacao, estado, rotulo, altura, arrastar = false }: {
  tipo: string; identificacao?: string; estado: Estado; rotulo: string; altura?: number; arrastar?: boolean;
}) {
  const caixa = useRef<HTMLDivElement>(null);
  const previa = useRef<Previa | null>(null);
  const [falhou, setFalhou] = useState<string | null>(null);
  const modelo = modeloDoAtivo(tipo, identificacao);
  const desbotado = estado === "semcom" || estado === "semtel";
  // a cena monta depois do import(): usa o estado mais recente, não o da renderização que pediu a montagem
  const atual = useRef({ modelo, desbotado, estado });
  atual.current = { modelo, desbotado, estado };

  useEffect(() => {
    const el = caixa.current;
    if (!el || !modelo) return;
    let cancelado = false;
    setFalhou(null);
    import("./previa3d").then(({ montarPrevia }) => {
      if (cancelado || previa.current) return;
      try {
        const agora = atual.current;
        if (!agora.modelo) return;
        previa.current = montarPrevia(el, { modelo: agora.modelo, desbotado: agora.desbotado, girar: true, arrastar,
          corAnel: cor(el, COR_ESTADO[agora.estado] ?? "--borda") },
          () => setFalhou("Modelo 3D não carregou"));
      } catch {
        setFalhou("Sem 3D neste navegador");
      }
    }).catch(() => setFalhou("Visualização 3D indisponível"));
    return () => { cancelado = true; previa.current?.parar(); previa.current = null; };
    // a cena é montada uma vez; trocas de ativo passam por `atualizar` (efeito abaixo)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(modelo)]);

  useEffect(() => {
    const el = caixa.current;
    if (!el || !modelo || !previa.current) return;
    previa.current.atualizar({ modelo, desbotado, corAnel: cor(el, COR_ESTADO[estado] ?? "--borda") });
  }, [modelo, desbotado, estado]);

  // o modo de exibição muda as cores dos tokens: o anel acompanha
  useEffect(() => {
    const obs = new MutationObserver(() => {
      if (caixa.current && previa.current) previa.current.atualizar({ corAnel: cor(caixa.current, COR_ESTADO[estado] ?? "--borda") });
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-modo"] });
    return () => obs.disconnect();
  }, [estado]);

  return (
    <div className="tl-cena" ref={caixa} style={altura ? { height: altura } : undefined}>
      <span className="tl-cena-rotulo">{rotulo}{arrastar && modelo && !falhou ? " · arraste para girar" : ""}</span>
      {(!modelo || falhou) && (
        <div className="tl-cena-reserva"><div><IconeTipo tipo={tipo} grande />{rotulo}<br /><small>{falhou ?? "Sem modelo 3D para este tipo"}</small></div></div>
      )}
    </div>
  );
}
